import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { useSubmitContactForm } from "../hooks/useSubmitContactForm";
import Icon from "../../../common/components/Icon/Icon";
import {
  createContactFormSchema,
  ContactFormData,
} from "../helpers/contactValidation";

const ContactForm: React.FC = () => {
  const { t } = useTranslation();
  const { submitContactForm, isLoading, isSuccess, isError } =
    useSubmitContactForm();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<ContactFormData>({
    resolver: zodResolver(createContactFormSchema(t)),
  });

  // --- TIMESTAMP LOGIC ---
  useEffect(() => {
    register("formLoadTimestamp");
    setValue("formLoadTimestamp", Math.floor(Date.now() / 1000));
  }, [register, setValue]);
  // --- END TIMESTAMP ---

  const onSubmit = async (data: ContactFormData) => {
    await submitContactForm(data);
  };

  if (isSuccess) {
    return (
      <div className="cf-success-state">
        <div className="cf-success-state__icon">
          <Icon iconName="check-circle" className="icon--filled" />
        </div>
        <h3 className="cf-success-state__title">
          {t("feedback.default.success", "Success!")}
        </h3>
        <p className="cf-success-state__message">
          {t(
            "contactForm.success",
            "Message sent successfully. We will get back to you soon."
          )}
        </p>
      </div>
    );
  }

  return (
    <form
      className={`cf-form ${isLoading ? "is-loading" : ""}`}
      onSubmit={handleSubmit(onSubmit)}
      noValidate
    >
      <div className="cf-form__header">
        <h2 className="cf-form__title">
          {t("contactForm.title", "Send Us a Message")}
        </h2>
      </div>

      {isError && (
        <div className="cf-form__alert cf-form__alert--error">
          <Icon iconName="exclamation-circle" className="icon--filled" />
          <span>
            {t(
              "contactForm.error",
              "Failed to send message. Please try again."
            )}
          </span>
        </div>
      )}

      <fieldset className="cf-form__fieldset" disabled={isLoading}>
        {/* Honeypot Field */}
        <div className="cf-form__honeypot" aria-hidden="true">
          <label htmlFor="cf_nickname">Nickname</label>
          <input
            id="cf_nickname"
            type="text"
            {...register("nickname")}
            tabIndex={-1}
            autoComplete="off"
          />
        </div>

        {/* Name Field */}
        <div className="cf-field">
          <label className="cf-field__label" htmlFor="cf-name">
            {t("contactForm.name", "Name Surname")}{" "}
            <span className="required-mark">*</span>
          </label>
          <input
            id="cf-name"
            type="text"
            {...register("name")}
            className={`cf-field__input ${errors.name ? "has-error" : ""}`}
          />
          {errors.name && (
            <p className="cf-field__error">{errors.name.message}</p>
          )}
        </div>

        {/* Email Field */}
        <div className="cf-field">
          <label className="cf-field__label" htmlFor="cf-email">
            {t("contactForm.email", "E-mail Address")}{" "}
            <span className="required-mark">*</span>
          </label>
          <input
            id="cf-email"
            type="email"
            {...register("email")}
            className={`cf-field__input ${errors.email ? "has-error" : ""}`}
          />
          {errors.email && (
            <p className="cf-field__error">{errors.email.message}</p>
          )}
        </div>

        {/* Subject Field */}
        <div className="cf-field">
          <label className="cf-field__label" htmlFor="cf-subject">
            {t("contactForm.subject", "Subject (Optional)")}
          </label>
          <input
            id="cf-subject"
            type="text"
            {...register("subject")}
            className="cf-field__input"
          />
        </div>

        {/* Message Field */}
        <div className="cf-field">
          <label className="cf-field__label" htmlFor="cf-message">
            {t("contactForm.message", "Your Message")}{" "}
            <span className="required-mark">*</span>
          </label>
          <textarea
            id="cf-message"
            rows={5}
            {...register("message")}
            className={`cf-field__input ${errors.message ? "has-error" : ""}`}
          />
          {errors.message && (
            <p className="cf-field__error">{errors.message.message}</p>
          )}
        </div>

        <button type="submit" className="cf-form__submit">
          {isLoading
            ? t("contactForm.sending", "Sending...")
            : t("contactForm.submit", "Send Message")}
        </button>
      </fieldset>
    </form>
  );
};

export default ContactForm;
